/**
 * Product Owner Dashboard Page
 * @implements TS-022 - Role-based Routing
 * @implements US-008b - Password Setup UI (Settings link)
 * @implements US-014 - Project Listing & Navigation
 *
 * Dashboard for Product Owners with project navigation.
 */

import {
  Box,
  Container,
  Typography,
  Paper,
  Button,
  AppBar,
  Toolbar,
} from '@mui/material';
import SettingsIcon from '@mui/icons-material/Settings';
import FolderIcon from '@mui/icons-material/Folder';
import { useNavigate } from 'react-router-dom';
import { useAuthContext } from '@/providers';
import { ProjectSelector } from '@/components/ProjectSelector';

/**
 * DashboardPOPage - Product Owner dashboard with project navigation
 *
 * @implements TS-022.3 - Placeholder dashboard pages
 * @implements US-014 - Project Listing & Navigation
 *
 * Features:
 * - ProjectSelector dropdown in header for quick project switching
 * - Navigation to project list and individual projects
 * - Settings and logout functionality
 */
export const DashboardPOPage = () => {
  const navigate = useNavigate();
  const { user, logout, isLoggingOut } = useAuthContext();

  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
      {/* Header with ProjectSelector */}
      <AppBar position="static" color="default" elevation={0}>
        <Toolbar sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Typography variant="h6" sx={{ flexGrow: 0, mr: 3 }}>
            Jared
          </Typography>

          {/* Project Selector */}
          <Box sx={{ mr: 2 }}>
            <ProjectSelector variant="outlined" />
          </Box>

          <Box sx={{ flexGrow: 1 }} />

          {/* Settings Button */}
          <Button
            color="inherit"
            startIcon={<SettingsIcon />}
            onClick={() => navigate('/settings')}
            sx={{ mr: 1 }}
          >
            Settings
          </Button>

          {/* Logout Button */}
          <Button color="inherit" onClick={logout} disabled={isLoggingOut}>
            {isLoggingOut ? 'Logging out...' : 'Logout'}
          </Button>
        </Toolbar>
      </AppBar>

      {/* Main Content */}
      <Container maxWidth="lg" sx={{ flexGrow: 1 }}>
        <Box sx={{ py: 4 }}>
          <Paper
            elevation={0}
            sx={{
              p: 4,
              textAlign: 'center',
              border: 1,
              borderColor: 'divider',
            }}
          >
            <Typography variant="h3" component="h1" gutterBottom>
              Product Owner Dashboard
            </Typography>
            <Typography variant="body1" color="text.secondary" paragraph>
              Welcome back,{' '}
              {user?.displayName || user?.email || 'Product Owner'}!
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
              Select a project from the dropdown above, or view all your
              projects below.
            </Typography>

            <Box
              sx={{ mt: 4, display: 'flex', gap: 2, justifyContent: 'center' }}
            >
              <Button
                variant="contained"
                startIcon={<FolderIcon />}
                onClick={() => navigate('/projects')}
              >
                View All Projects
              </Button>
            </Box>
          </Paper>
        </Box>
      </Container>
    </Box>
  );
};
